import { Component } from '@theme/component';

/** Start loading this far before the component scrolls into view. */
const ROOT_MARGIN = '400px';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} [videoElement] - The video element.
 * @property {HTMLVideoElement[]} [videoElements] - The video elements.
 * @property {HTMLButtonElement} [playPauseButton] - The play/pause toggle.
 * @property {HTMLButtonElement} [muteButton] - The mute/unmute toggle.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** Sources are only ever attached once. */
  #hydrated = false;

  connectedCallback() {
    super.connectedCallback();

    for (const videoElement of this.videoElements) {
      videoElement.addEventListener('play', () => this.updateControls());
      videoElement.addEventListener('pause', () => this.updateControls());
      videoElement.addEventListener('volumechange', () => this.updateControls());
      videoElement.addEventListener('canplay', () => this.classList.add('video-background-component--ready'));
    }

    this.updateControls();

    // ECR-180: this used to attach every `src` and call `load()` right here.
    // Moving the URL into `data-video-source` looks like lazy loading, but it
    // only moved the fetch from the HTML parser to script execution — every
    // background video on the page still started streaming at once. Measured on
    // the homepage: 5.29 MB of HLS across 26 requests, 62% of total page weight,
    // for three videos of which one is visible.
    //
    // Now the sources are attached when the component approaches the viewport.
    // A component that is already in view (the hero) hydrates on the observer's
    // first callback, which lands in the next frame, so the visible video is not
    // meaningfully delayed. The `poster` still renders in the meantime.
    if (!('IntersectionObserver' in window)) {
      this.#hydrate();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#hydrate();
      },
      { rootMargin: ROOT_MARGIN }
    );

    this.#observer.observe(this);
  }

  /** Attaches the deferred sources and starts loading. Safe to call repeatedly. */
  #hydrate() {
    if (this.#hydrated) return;
    this.#hydrated = true;

    this.#observer?.disconnect();
    this.#observer = null;

    const { videoSources } = this.refs;

    for (const source of videoSources ?? []) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    for (const videoElement of this.videoElements) {
      videoElement.load();
    }

    this.updateControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;
  }

  get videoElements() {
    const { videoElement, videoElements } = this.refs;

    return /** @type {HTMLVideoElement[]} */ (
      videoElements ?? (videoElement ? [videoElement] : [])
    );
  }

  get activeVideoElement() {
    return this.videoElements.find((videoElement) => getComputedStyle(videoElement).display !== 'none') ?? this.videoElements[0];
  }

  togglePlayback() {
    // A visitor can reach the play button before the component has scrolled
    // into view, for example via keyboard. Attach the sources first, otherwise
    // play() would be called on a video that has none.
    this.#hydrate();

    const videoElement = this.activeVideoElement;

    if (!videoElement) return;

    if (videoElement.paused) {
      videoElement.play();
    } else {
      videoElement.pause();
    }
  }

  toggleMute() {
    const videoElement = this.activeVideoElement;

    if (!videoElement) return;

    videoElement.muted = !videoElement.muted;
    this.updateControls();
  }

  updateControls() {
    const { playPauseButton, muteButton } = this.refs;
    const videoElement = this.activeVideoElement;

    if (!videoElement) return;

    const playPauseLabel = videoElement.paused
      ? playPauseButton?.dataset.playLabel
      : playPauseButton?.dataset.pauseLabel;

    if (playPauseLabel) playPauseButton?.setAttribute('aria-label', playPauseLabel);
    playPauseButton?.classList.toggle('is-paused', videoElement.paused);
    muteButton?.setAttribute('aria-pressed', videoElement.muted ? 'true' : 'false');
    muteButton?.classList.toggle('is-muted', videoElement.muted);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
